/**
 * Bloqueios da marca (14/10/2026) — `docs/app-unificado/PLANO-framework-de-bloqueio.md`.
 *
 * O que a marca restringe para as pacientes dela, em regras. Cada regra
 * responde a cinco perguntas: o que fica bloqueado (`alvo`), quando
 * (`vigencia`), como aparece (`apresentacao` e `aviso`) e o que libera
 * (`liberacao`). Para quem: equipe e profissionais nunca são bloqueados.
 *
 * Não confundir com:
 * - funcionalidade (`enabledFeatures`): o que a marca contratou, decidido
 *   pelo mantenedor; desligada, some;
 * - barreira (`barriers`): o formulário de adesão de antes, que segue
 *   valendo enquanto não for absorvido por aqui (fase 3 do plano);
 * - lista branca: curadoria de conteúdo por pessoa.
 *
 * A avaliação é pura (`avaliarBloqueios`): a API decide com ela e aplica em
 * cada porta; o app só desenha o que a API devolve.
 */
import { z } from 'zod';
import { ButtonActionSchema } from './button-action.schema';

/**
 * As jornadas, no vocabulário do app (onboarding e troca de fase).
 * `PARCEIRO` é escolher acompanhar alguém; quem acompanha também é barrado
 * pela jornada de quem acompanha.
 */
export const JornadaBloqueavelEnum = z.enum(['TENTANTE', 'GESTANTE', 'PUERPERA', 'MAE', 'MAE_DE_ANJO', 'ADOTANTE', 'PARCEIRO']);
export type JornadaBloqueavel = z.infer<typeof JornadaBloqueavelEnum>;

/**
 * O que fica bloqueado. Catálogo fechado: cada alvo tem uma porta conhecida
 * na API e no app. Alvo novo entra aqui junto com a porta dele.
 */
export const AlvoDeBloqueioSchema = z.discriminatedUnion('tipo', [
  /** Escolher estas jornadas (no onboarding e na troca de fase). Quem já está numa delas continua. */
  z.object({ tipo: z.literal('JORNADA'), jornadas: z.array(JornadaBloqueavelEnum).min(1) }),
  /** Os canais de chat escolhidos; vazio = todos. */
  z.object({ tipo: z.literal('CHAT'), canais: z.array(z.string()).default([]) }),
  /** O agendamento de consultas. */
  z.object({ tipo: z.literal('AGENDAMENTO') }),
]);
export type AlvoDeBloqueio = z.infer<typeof AlvoDeBloqueioSchema>;

/** Quando a regra vale. Datas em ISO 8601. */
export const VigenciaDeBloqueioSchema = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('SEMPRE') }),
  z.object({ tipo: z.literal('PERIODO'), de: z.string().nullable().default(null), ate: z.string().nullable().default(null) }),
]);
export type VigenciaDeBloqueio = z.infer<typeof VigenciaDeBloqueioSchema>;

/**
 * Como aparece: `TRAVAR` mostra o item com cadeado e, no toque, o aviso;
 * `OCULTAR` faz o item sumir.
 */
export const ApresentacaoDeBloqueioEnum = z.enum(['TRAVAR', 'OCULTAR']);
export type ApresentacaoDeBloqueio = z.infer<typeof ApresentacaoDeBloqueioEnum>;

/** O que a pessoa lê ao tocar num item travado. */
export const AvisoDeBloqueioSchema = z.object({
  titulo: z.string().trim().min(1).max(120),
  texto: z.string().trim().min(1).max(600),
  /**
   * O botão sobre o item travado, onde a tela tem um ("Liberar jornada", no
   * "Continuar" do onboarding quando a jornada do centro está travada). Tocar
   * abre o aviso. Sem ele, o botão da tela fica com o rótulo de sempre.
   */
  chamada: z.string().trim().max(40).nullable().default(null),
  /** Um botão opcional; a ação é a mesma do editor da home (abrir formulário, link…). */
  botao: z.object({ rotulo: z.string().trim().min(1).max(40), acao: ButtonActionSchema }).nullable().default(null),
  /** O rótulo de fechar o aviso. */
  fechar: z.string().trim().min(1).max(40).default('Agora não'),
});
export type AvisoDeBloqueio = z.infer<typeof AvisoDeBloqueioSchema>;

/**
 * O que tira a pessoa do bloqueio:
 * - `NUNCA`: nada dentro da marca (o benefício pode morar em outra marca, como
 *   na Eva Maternidade, onde cada empresa cliente é uma marca — A062);
 * - `EMPRESA_VINCULADA`: chegar por uma empresa da marca (vínculo com `companyId`).
 */
export const LiberacaoDeBloqueioEnum = z.enum(['NUNCA', 'EMPRESA_VINCULADA']);
export type LiberacaoDeBloqueio = z.infer<typeof LiberacaoDeBloqueioEnum>;

export const RegraDeBloqueioSchema = z.object({
  id: z.string().min(1),
  /** Para o painel ("Jornadas do benefício"); a paciente não vê. */
  nome: z.string().trim().min(1).max(80),
  ativa: z.boolean().default(true),
  alvo: AlvoDeBloqueioSchema,
  vigencia: VigenciaDeBloqueioSchema.default({ tipo: 'SEMPRE' }),
  apresentacao: ApresentacaoDeBloqueioEnum.default('TRAVAR'),
  aviso: AvisoDeBloqueioSchema,
  liberacao: LiberacaoDeBloqueioEnum.default('NUNCA'),
});
export type RegraDeBloqueio = z.infer<typeof RegraDeBloqueioSchema>;

/** O que a regra decide para uma pessoa: vai para o app e no 403 `BLOQUEADO`. */
export interface BloqueioAtivo {
  regraId: string;
  alvo: AlvoDeBloqueio;
  apresentacao: ApresentacaoDeBloqueio;
  aviso: AvisoDeBloqueio;
}

/** O que a avaliação precisa saber da pessoa. */
export interface PessoaParaBloqueio {
  /** Equipe, profissional, gestora: nunca é bloqueada. */
  ehPaciente: boolean;
  /** Chegou por uma empresa da marca (vínculo de paciente com `companyId`). */
  temEmpresa: boolean;
}

function vigente(v: VigenciaDeBloqueio, agora: Date): boolean {
  if (v.tipo === 'SEMPRE') return true;
  const t = agora.getTime();
  if (v.de && t < new Date(v.de).getTime()) return false;
  if (v.ate && t >= new Date(v.ate).getTime()) return false;
  return true;
}

/** Os bloqueios que valem para a pessoa agora. */
export function avaliarBloqueios(regras: readonly RegraDeBloqueio[] | null | undefined, pessoa: PessoaParaBloqueio, agora = new Date()): BloqueioAtivo[] {
  if (!pessoa.ehPaciente || !regras?.length) return [];
  return regras
    .filter((r) => r.ativa && vigente(r.vigencia, agora))
    .filter((r) => !(r.liberacao === 'EMPRESA_VINCULADA' && pessoa.temEmpresa))
    .map((r) => ({ regraId: r.id, alvo: r.alvo, apresentacao: r.apresentacao, aviso: r.aviso }));
}

/** A escolha de jornada: a própria, ou acompanhar a de alguém. */
export interface EscolhaDeJornada {
  jornada: JornadaBloqueavel;
  /** Com `PARCEIRO`: a jornada de quem acompanha. */
  acompanha?: JornadaBloqueavel | null;
}

/** O bloqueio que impede essa escolha, se houver. Acompanhar é barrado pela jornada acompanhada. */
export function bloqueioDaJornada(ativos: readonly BloqueioAtivo[], escolha: EscolhaDeJornada): BloqueioAtivo | null {
  return (
    ativos.find((b) => {
      if (b.alvo.tipo !== 'JORNADA') return false;
      const travadas = b.alvo.jornadas;
      if (travadas.includes(escolha.jornada)) return true;
      return escolha.jornada === 'PARCEIRO' && !!escolha.acompanha && travadas.includes(escolha.acompanha);
    }) ?? null
  );
}

/** O bloqueio do chat (de um canal, se dado) ou do agendamento, se houver. */
export function bloqueioDoRecurso(ativos: readonly BloqueioAtivo[], recurso: { tipo: 'CHAT'; canalId?: string | null } | { tipo: 'AGENDAMENTO' }): BloqueioAtivo | null {
  return (
    ativos.find((b) => {
      if (recurso.tipo === 'AGENDAMENTO') return b.alvo.tipo === 'AGENDAMENTO';
      if (b.alvo.tipo !== 'CHAT') return false;
      return !b.alvo.canais.length || (!!recurso.canalId && b.alvo.canais.includes(recurso.canalId));
    }) ?? null
  );
}

/**
 * As jornadas que são da própria pessoa, para ver o conteúdo delas (14/10/2026,
 * item 22 da design): a do cadastro e as que o app deriva dela com o tempo —
 * quem está grávida vira puérpera e, 42 dias depois do parto, "pós licença"
 * (`MAE`) sem trocar o cadastro. O parceiro tem as de quem ele acompanha.
 * Uma porta que olhasse só o cadastro travaria a jornada da própria pessoa.
 */
export function jornadasDaPessoa(p: { userType?: string | null; fatherFigureFor?: string | null }): JornadaBloqueavel[] {
  const linha = (t: string | null | undefined): JornadaBloqueavel[] => {
    switch (t) {
      case 'PREGNANT':
        return ['GESTANTE', 'PUERPERA', 'MAE'];
      case 'POSTPARTUM':
        return ['PUERPERA', 'MAE'];
      case 'TENTANTE':
      case 'MAE_DE_ANJO':
      case 'ADOTANTE':
      case 'MAE':
        return [t];
      default:
        return [];
    }
  };
  return p.userType === 'FATHER_FIGURE' ? linha(p.fatherFigureFor) : linha(p.userType);
}

/**
 * A jornada no vocabulário do app a partir do que a API grava: `userType`,
 * `fatherFigureFor` e, para gestante, se a gestação já terminou.
 */
export function jornadaDoPerfil(p: { userType?: string | null; fatherFigureFor?: string | null; gestacaoConcluida?: boolean }): EscolhaDeJornada | null {
  const daApi = (t: string | null | undefined, concluida?: boolean): JornadaBloqueavel | null => {
    switch (t) {
      case 'PREGNANT':
        return concluida ? 'PUERPERA' : 'GESTANTE';
      case 'POSTPARTUM':
        return 'PUERPERA';
      case 'TENTANTE':
      case 'MAE_DE_ANJO':
      case 'ADOTANTE':
      case 'MAE':
        return t;
      default:
        return null;
    }
  };
  if (p.userType === 'FATHER_FIGURE') return { jornada: 'PARCEIRO', acompanha: daApi(p.fatherFigureFor) };
  const jornada = daApi(p.userType, p.gestacaoConcluida);
  return jornada ? { jornada } : null;
}
